/**
 * Wraps a value so that it always falls within min/max,
 * where a value greater than max gets set to min, and vice versa
 */
export function wrap(val: number, min: number, max: number) {
  if (val > max) {
    return min
  }
  if (val < min) {
    return max
  }
  return val
}

/**
 * Clamps a value so that it always falls within min/max,
 * where a value greater than max gets set to min, and vice versa
 */
export function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max)
}

/**
 * Transforms any number to a padded version, meaning a single
 * digit number is converted to `0X` format, where X is the digit.
 *
 * Note: The result is no longer a number but a string
 * @param {number} val
 * @returns {string}
 */
export function pad(val: number, digitsAmount: number = 2): string {
  return `${Math.floor(Math.abs(val))}`.padStart(digitsAmount, '0')
}

/**
 * Function for gaussian rounding that is also used with invoice row calculations.
 * Taken from Legacy Repository's `item_price_script.js`
 */
export function roundEven(value: number, decimalPlaces = 0) {
  const multiplier = Math.pow(10, decimalPlaces)
  const num = +(decimalPlaces ? value * multiplier : value).toFixed(8) // Avoid rounding errors
  const integerPart = Math.floor(num)
  const fractionalPart = num - integerPart
  const e = 1e-8 // Allow for rounding errors in remainder
  const shouldRoundEven = fractionalPart > 0.5 - e && fractionalPart < 0.5 + e
  let rounded: number
  if (shouldRoundEven) {
    if (integerPart % 2 === 0) {
      // Integer part ends with even number -> keep it as is
      rounded = integerPart
    } else {
      // Integer part ends with odd number -> increment to next even
      rounded = integerPart + 1
    }
  } else {
    rounded = Math.round(num)
  }
  return decimalPlaces ? rounded / multiplier : rounded
}

/**
 * Validates if a string is a valid numeric value
 * @param string The string to validate
 * @param options Configuration options
 * @param options.allowNegative Whether to allow negative numbers (defaults to false)
 * @param options.allowDecimal Whether to allow decimal numbers (defaults to false)
 * @returns true if the string is a valid numeric value, false otherwise
 *
 * @example
 * // Basic usage (only positive integers allowed by default)
 * isValidNumericString('123')           // true
 * isValidNumericString('-123')          // false
 * isValidNumericString('123.45')        // false
 *
 * // With allowNegative option
 * isValidNumericString('-123', { allowNegative: true })          // true
 * isValidNumericString('-123.45', { allowNegative: true })       // false
 *
 * // With allowDecimal option
 * isValidNumericString('123.45', { allowDecimal: true })         // true
 * isValidNumericString('123.', { allowDecimal: true })           // true (trailing decimal allowed)
 * isValidNumericString('-123.45', { allowDecimal: true })        // true (negative allowed with decimal)
 *
 * // With both options
 * isValidNumericString('-123.45', { allowNegative: true, allowDecimal: true })  // true
 * isValidNumericString('-.5', { allowNegative: true, allowDecimal: true })      // true
 * isValidNumericString('-123.', { allowNegative: true, allowDecimal: true })    // true
 */
export function isValidNumericString(
  string: string,
  options: {
    allowNegative?: boolean
    allowDecimal?: boolean
  } = {
    allowNegative: false,
    allowDecimal: false,
  },
) {
  if (typeof string === 'number') {
    return true
  }

  if (typeof string !== 'string') {
    return false
  }

  if (!string?.trim()?.length) {
    return false
  }

  // If decimals are not allowed, no dots are allowed.
  // If they are allowed, there can be only one dot.
  const numberOfDots = string?.split('.').length - 1
  if (
    (numberOfDots && !options?.allowDecimal) ||
    (options?.allowDecimal && numberOfDots > 1)
  ) {
    return false
  }

  // If allows decimal and last character is a dot, remove it so the number
  // gets validated as a whole number
  const lastChar = string?.slice(-1)
  if (options?.allowDecimal && lastChar === '.') {
    string = string.slice(0, -1)
  }

  // Validate first character
  const validDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
  const firstChar = string?.slice(0, 1)
  if (!['-', '.', ...validDigits].includes(firstChar)) {
    return false
  }

  // Validate last character
  if (!['.', ...validDigits].includes(lastChar)) {
    return false
  }

  // Check for forbidden characters that Number() would accept.
  const forbiddenCharacters = [
    'x', // Hexadecimal
    'b', // Binary
    'e', // Scientific notation
  ]
  if (forbiddenCharacters.some((character) => string?.includes(character))) {
    return false
  }

  const numberValue = Number(string)
  return (
    !isNaN(numberValue) &&
    (!options?.allowNegative ? numberValue >= 0 : true) &&
    (!options?.allowDecimal ? Number.isInteger(numberValue) : true)
  )
}

/**
 * Converts a value to a number with fallback to 0.
 *
 * This function provides a safer alternative to Number() by handling edge cases gracefully:
 * - Similar to Number() for valid numeric strings, but rejects non-decimal notation
 * - Returns a fallback value (default: 0) for null, undefined, and non-valid numbers
 * - Avoids NaN results which can break calculations or display incorrectly
 *
 * Key behaviors:
 * - Valid numeric strings are converted to numbers: "123" → 123, including trimming whitespace
 * - Supports negative and decimal numbers (with dot as decimal separator, just like Number())
 * - Number values remain unchanged (except NaN, which returns fallback)
 * - Empty strings, whitespace, or non-numeric strings return fallback
 * - null and undefined return fallback
 * - Unlike Number(), scientific notation ("1e3"), hex ("0xFF"), and binary ("0b1010") return fallback
 *
 * This is particularly useful to replace patterns where we want to convert a value to a number,
 * but we want to handle edge cases gracefully – especially the ones where Number() returns NaN. Example:
 * ```
 * const count = Number(data?.count) // NaN
 * ```
 *
 * With the cleaner:
 * ```
 * const count = toNumber(data?.count) // 0
 * ```
 *
 * @example
 * // Basic usage
 * toNumber(5)          // 5
 * toNumber(5.5)        // 5.5
 * toNumber(-5.5)       // -5.5
 * toNumber("5")        // 5
 * toNumber("5.5")      // 5.5
 * toNumber("-5.5")     // -5.5
 * toNumber(" -5.5 ")   // -5.5
 * toNumber("abc")      // 0
 * toNumber(null)       // 0
 * toNumber(undefined)  // 0
 * toNumber(NaN)        // 0
 * toNumber([])         // 0
 * toNumber({})         // 0
 * toNumber(true)       // 0
 * toNumber(false)      // 0
 * toNumber("true")     // 0
 * toNumber("false")    // 0
 * toNumber("1e3")      // 0 (scientific notation not supported)
 * toNumber("0xFF")     // 0 (hex notation not supported)
 *
 * @param value The value to convert to a number
 */
export function toNumber(value: unknown): number {
  const fallbackValue = 0

  // Return fallback for non-string/number values
  if (!['string', 'number'].includes(typeof value)) {
    return fallbackValue
  }

  // If it's already a number, return it or fallback if NaN
  if (typeof value === 'number') {
    return isNaN(value) ? fallbackValue : value
  }

  // Return fallback for empty strings
  const stringValue = value?.toString().trim()
  if (!stringValue?.length) {
    return fallbackValue
  }

  // Convert string to number
  const isValidNumber = isValidNumericString(stringValue, {
    allowNegative: true,
    allowDecimal: true,
  })

  return isValidNumber ? Number(stringValue) : fallbackValue
}

/**
 * Cryptographically secure replacement for Math.random()
 * Returns a random number between 0 (inclusive) and 1 (exclusive)
 *
 * This function is needed because Math.random() is not cryptographically secure.
 * It uses a pseudo-random number generator (PRNG) that can be predicted or manipulated.
 * This makes it vulnerable to security issues, specifically CWE-338 (Use of Cryptographically
 * Weak Pseudo-Random Number Generator).
 *
 * Alternatively, this function uses the Web Crypto API's getRandomValues() which provides
 * cryptographically strong random values. While this might be overkill for simple
 * randomization needs, it's a safer choice that prevents potential security issues.
 *
 * @example
 * safeMathRandom() // => 0.123456789
 * safeMathRandom() // => 0.987654321
 * safeMathRandom() // => 0.456789123
 *
 * // Common usage with Math.floor for random integers
 * Math.floor(safeMathRandom() * 10) // => 7 (random number between 0-9)
 * Math.floor(safeMathRandom() * 100) // => 42 (random number between 0-99)
 */
export function safeMathRandom() {
  const cryptoObj = crypto || (window as { msCrypto?: Crypto }).msCrypto
  return cryptoObj.getRandomValues(new Uint32Array(1))[0] / 4294967296
}

/**
 * Universal function to apply or remove a percentage to/from a value.
 *
 * When discount is false: multiplies by (1 + rate)
 * When discount is true: divides by (1 + rate)
 *
 * Note: To remove a percentage, divide by (1 + rate), not subtract.
 * The formula `value × (1 - rate)` is mathematically incorrect.
 *
 * @param value The value to transform
 * @param percentage The percentage (e.g., 20 for 20%, -10 for -10%)
 * @param discount If true, removes the percentage from value (default: true)
 * @returns The transformed value
 *
 * @example
 * // Remove 25% VAT from $125 (discount mode, default)
 * setPercentage(125, 25)  // 100
 *
 * @example
 * // Add 30% VAT to $150
 * setPercentage(150, 30, false)  // 195
 *
 * @example
 * // With 0% nothing changes
 * setPercentage(75, 0)  // 75
 * setPercentage(75, 0, false)  // 75
 */
export function setPercentage(
  value: number,
  percentage: number,
  discount?: boolean,
): number {
  if (percentage === 0) {
    return value
  }

  const ratio = 1 + percentage / 100
  const shouldDiscount = discount ?? true

  return shouldDiscount ? value / ratio : value * ratio
}
